/* eslint-disable max-lines */
// src/services/api.js
import {auth} from '../config/firebase';
import { getApiUrl } from '../config/apiUrls';

/**
 * Helper function to get the current authentication token
 * @returns {Promise<string>} The ID token
 * @throws {Error} If user is not authenticated
 */
const getAuthToken = async () => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }
    try {
        // Don't force refresh - use cached token if valid
        return await user.getIdToken();
    } catch (error) {
        // Handle quota exceeded specifically
        if (error.code === 'auth/quota-exceeded') {
            console.error('Firebase auth quota exceeded. Using cached token if available.');
            // Try to get the token without forcing refresh
            const token = user.accessToken;
            if (token) {
                return token;
            }
        }
        throw error;
    }
};

/**
 * Helper for consistent fetch handling with authentication
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} JSON response
 * @throws {Error} If the request fails
 */
const fetchWithAuth = async (url, options = {}, _retryCount = 0, customTimeout = 30000) => {
    try {
        // Get authentication token
        const token = await getAuthToken();

        console.log(`Making authenticated request to: ${url}`);

        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), customTimeout); // Configurable timeout

        // Build headers
        const headers = {
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {})
        };

        // Only add Content-Type for requests with body
        if (options.body) {
            headers['Content-Type'] = 'application/json';
        }

        // Make request with auth token
        const response = await fetch(url, {
            ...options,
            credentials: 'omit', // Don't send cookies with cross-origin requests
            headers,
            mode: 'cors', // Explicitly specify CORS mode
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        console.log(`Response status: ${response.status} ${response.statusText}`);
        console.log('Response headers:', [...response.headers.entries()]);

        // Handle non-2xx responses
        if (!response.ok) {
            // Try to parse error response
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || `Error: ${response.status} ${response.statusText}`;
            } catch (_e) {
                errorMessage = `Error: ${response.status} ${response.statusText}`;
            }
            throw new Error(errorMessage);
        }

        // Parse response as JSON
        const responseData = await response.json();
        // Only log for debugging specific endpoints
        if (url.includes('/shared-drives/') && (url.includes('/contents') || url.includes('/folders/'))) {
            console.log(`[${new Date().toISOString()}] Response from ${url}:`, responseData);
        }
        return responseData;
    } catch (error) {
        console.error('API request failed:', error);

        // Handle timeout specifically
        if (error.name === 'AbortError') {
            console.error('Request timed out after 30 seconds');
            throw new Error('Request timed out. Please check your connection and try again.');
        }

        // No retries - just log the error and continue
        console.log('API request failed:', error.message);

        // If we've exceeded retries or it's not a retriable error, rethrow
        throw error;
    }
};

/**
 * API service for Faana Growth OS
 */
const api = {
    // Auth methods
    checkAuthStatus: async () => {
        return auth.currentUser !== null;
    },

    // Client methods
    getClients: async () => {
        // Get all clients the user has access to
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/list`;
        const response = await fetchWithAuth(functionUrl);

        // The backend returns { clients: [...] }, we need to return just the array
        // Also map clientId to id for consistency
        return response.clients ? response.clients.map(client => ({
            id: client.clientId,
            name: client.name,
            status: client.status,
            createdAt: client.createdAt,
            tier: client.tier,
            region: client.region,
            resources: client.resources
        })) : [];
    },

    getClientById: async (clientId) => {
        // Get specific client by ID using the get endpoint
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/get?entityId=${clientId}`;
        const response = await fetchWithAuth(functionUrl);

        // Map the response to match the expected format
        if (response && response.entity) {
            return {
                id: response.entity.clientId,
                name: response.entity.name,
                status: response.entity.status,
                createdAt: response.entity.createdAt,
                tier: response.entity.tier,
                region: response.entity.region,
                industry: response.entity.industry,
                resources: response.entity.resources
            };
        }
        return response;
    },

    createClient: async (clientData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/provision`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(clientData)
        });
    },

    updateClient: async (clientId, updateData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/update-client`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify({
                clientId,
                ...updateData
            })
        });
    },

    deleteClient: async (clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/delete-client`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE',
            body: JSON.stringify({
                clientId
            })
        });
    },

    // Public endpoints that don't require authentication
    getPublicData: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/public_endpoint`;

        // For public endpoints, don't use fetchWithAuth
        const response = await fetch(functionUrl, {
            method: 'GET',
            mode: 'cors', // Explicitly specify CORS mode
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    },

    // Test authentication with Firebase Functions
    testAuth: async () => {
        // Always try real authentication
        // No special mock handling for development mode

        // In production, make the real API call
        const functionUrl = `${getApiUrl('authWorkspace')}/api/auth/test`;

        try {
            console.log('testAuth: Starting auth test request');
            const data = await fetchWithAuth(functionUrl, {
                method: 'POST',
                body: JSON.stringify({})
            });
            console.log('testAuth: Received response:', data);
            if (data.error) {
                throw new Error(data.error);
            }
            // Return the full response data
            return data;
        } catch (error) {
            console.error('Auth test failed:', error);
            throw error;
        }
    },

    // Workspace API
    getWorkspaceUsers: async (maxResults = 100, query = null, pageToken = null, clientId = null) => {
        let url = `${getApiUrl('authWorkspace')}/api/auth/workspace/users?maxResults=${maxResults}`;

        if (query) {
            url += `&query=${encodeURIComponent(query)}`;
        }

        if (pageToken) {
            url += `&pageToken=${encodeURIComponent(pageToken)}`;
        }

        if (clientId) {
            url += `&entityId=${encodeURIComponent(clientId)}`;
        }

        return fetchWithAuth(url)
            .then(data => {
                if (data.error) {
                    throw new Error(data.error);
                }
                return data;
            })
            .catch(error => {
                console.error('Failed to get workspace users:', error);
                throw error;
            });
    },

    // Update user roles
    updateUserRoles: async (userId, roles) => {
        const functionUrl = `${getApiUrl('authWorkspace')}/api/auth/workspace/roles`;

        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({
                userId,
                roles
            })
        })
        .then(data => {
            if (data.error) {
                throw new Error(data.error);
            }
            return data;
        })
        .catch(error => {
            console.error('Failed to update user roles:', error);
            throw error;
        });
    },

    // Client permissions
    getClientPermissions: async (clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/permissions`;

        const data = await fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId })
        });
        if (data.error) {
            throw new Error(data.error);
        }
        // Return the permissions object from the response
        return data.permissions || { roles: {}, userPermissions: {} };
    },

    // Update client permissions
    updateClientPermissions: async (permissionData) => {
        try {
            const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/permissions/update`;

            const response = await fetchWithAuth(functionUrl, {
                method: 'POST',
                body: JSON.stringify(permissionData)
            });

            if (response.error) {
                throw new Error(response.error);
            }

            return response;
        } catch (error) {
            console.error('Failed to update client permissions:', error);
            throw error;
        }
    },

    // Delete client permission
    deleteClientPermission: async (clientId, userId) => {
        try {
            const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/permissions/delete`;

            const response = await fetchWithAuth(functionUrl, {
                method: 'DELETE',
                body: JSON.stringify({ entityId: clientId, userId })
            });

            if (response.error) {
                throw new Error(response.error);
            }

            return response;
        } catch (error) {
            console.error('Failed to delete client permission:', error);
            throw error;
        }
    },

    // Data Management API
    getClientGCSInfo: async (clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/dataManagementApi/api/data/gcs`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId })
        });
    },

    getClientBigQueryInfo: async (clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/dataManagementApi/api/data/bigquery`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId })
        });
    },

    getClientFirestoreInfo: async (clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/dataManagementApi/api/data/firestore`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId })
        });
    },

    getClientDataSummary: async (clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/dataManagementApi/api/data/summary`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId })
        });
    },

    // Entity-generic aliases for Data Management (maps to client backend for now)
    getEntityGCSInfo: async (entityId) => {
        return api.getClientGCSInfo(entityId);
    },

    getEntityBigQueryInfo: async (entityId) => {
        return api.getClientBigQueryInfo(entityId);
    },

    getEntityFirestoreInfo: async (entityId) => {
        return api.getClientFirestoreInfo(entityId);
    },

    // Trigger PTL processing for an entity's GCS files
    triggerPTLProcessing: async (entityId) => {
        const apiUrl = getApiUrl('ptl');  // Use 'ptl' not 'ptlApi'
        return fetchWithAuth(`${apiUrl}/api/ptl/process`, {
            method: 'POST',
            body: JSON.stringify({
                entityId: entityId,
                forceReprocess: false,
                triggerSource: 'ui_manual_trigger'
            })
        });
    },

    getEntityDataSummary: async (entityId) => {
        return api.getClientDataSummary(entityId);
    },

    // Get user roles by email
    getUserRolesByEmail: async (email, clientId) => {
        const functionUrl = `${getApiUrl('authWorkspace')}/api/auth/users/roles?email=${encodeURIComponent(email)}&entityId=${encodeURIComponent(clientId)}`;
        return fetchWithAuth(functionUrl);
    },

    // Get user photo URL (returns the function URL for the image)
    getUserPhotoUrl: (email) => {
        return `${getApiUrl('authWorkspace')}/api/auth/users/photo?email=${encodeURIComponent(email)}`;
    },

    // Enable FGOS access for a user
    enableFgosAccess: async (email) => {
        const functionUrl = `${getApiUrl('authWorkspace')}/api/auth/access/enable`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },

    // Disable FGOS access for a user
    disableFgosAccess: async (email) => {
        const functionUrl = `${getApiUrl('authWorkspace')}/api/auth/access/disable`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },

    // Update client user roles
    updateClientUserRoles: async (clientId, userEmail, roles) => {
        const functionUrl = `${getApiUrl('authWorkspace')}/api/auth/entities/${clientId}/roles`;
        const requestBody = { entityId: clientId, userEmail, roles };
        console.log('updateClientUserRoles - sending request:', {
            url: functionUrl,
            body: requestBody
        });
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });
    },

    // Ingestion Points APIs
    createIngestionPoint: async (clientId, data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/points`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId, ...data })
        });
    },

    listIngestionPoints: async (clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/points/list`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId })
        });
    },

    updateIngestionPoint: async (clientId, data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/points/update`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify({ entityId: clientId, ...data })
        });
    },

    deleteIngestionPoint: async (clientId, ingestionPointId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/points/delete`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE',
            body: JSON.stringify({ entityId: clientId, ingestionPointId })
        });
    },

    searchDriveFolders: async (query, clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/drive/search`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ query: query || '', entityId: clientId })
        });
    },

    // Google Forms APIs
    searchGoogleForms: async (query, clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/forms/search`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ query: query || '', entityId: clientId })
        });
    },

    verifyFormAccess: async (formId, clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/forms/verify`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ formId, entityId: clientId })
        });
    },

    syncIngestionPoint: async (clientId, ingestionPointId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/sync/manual`;
        // Use 5-minute timeout for sync operations (300000ms)
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId, ingestionPointId })
        }, 0, 300000);
    },

    snapshotIngestionPoint: async (clientId, ingestionPointId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/points/snapshot`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId, ingestionPointId })
        }, 0, 300000);
    },

    getIngestionManifest: async (clientId, ingestionPointId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/manifests`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId, ingestionPointId })
        });
    },

    getSyncOperation: async (clientId, syncOperationId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/sync/operation`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId, syncOperationId })
        });
    },

    uploadAudioToGCS: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/audio`;
        // Use 5-minute timeout for large audio uploads (300000ms)
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        }, 0, 300000);
    },

    // Entity-generic aliases for Ingestion Points (maps to client backend for now)
    createEntityIngestionPoint: async (entityId, data) => {
        return api.createIngestionPoint(entityId, data);
    },

    listEntityIngestionPoints: async (entityId) => {
        return api.listIngestionPoints(entityId);
    },

    updateEntityIngestionPoint: async (entityId, data) => {
        return api.updateIngestionPoint(entityId, data);
    },

    deleteEntityIngestionPoint: async (entityId, ingestionPointId) => {
        return api.deleteIngestionPoint(entityId, ingestionPointId);
    },

    syncEntityIngestionPoint: async (entityId, ingestionPointId) => {
        return api.syncIngestionPoint(entityId, ingestionPointId);
    },

    snapshotEntityIngestionPoint: async (entityId, ingestionPointId) => {
        return api.snapshotIngestionPoint(entityId, ingestionPointId);
    },

    getEntityIngestionManifest: async (entityId, ingestionPointId) => {
        return api.getIngestionManifest(entityId, ingestionPointId);
    },

    getEntitySyncOperation: async (entityId, syncOperationId) => {
        return api.getSyncOperation(entityId, syncOperationId);
    },

    // Entity-generic aliases for User Management (maps to client backend for now)
    getEntityUserRolesByEmail: async (email, entityId) => {
        return api.getUserRolesByEmail(email, entityId);
    },

    updateEntityUserRoles: async (entityId, userEmail, roles) => {
        return api.updateClientUserRoles(entityId, userEmail, roles);
    },

    // Entity-generic alias for permissions (maps to client backend for now)
    getEntityPermissions: async (entityId) => {
        return api.getClientPermissions(entityId);
    },

    // Grant platform admin privileges
    grantPlatformAdmin: async (email) => {
        const functionUrl = `${getApiUrl('authWorkspace')}/api/auth/platform-admin/grant`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },

    // Revoke platform admin privileges
    revokePlatformAdmin: async (email) => {
        const functionUrl = `${getApiUrl('authWorkspace')}/api/auth/platform-admin/revoke`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ email })
        });
    },

    // Document Management APIs
    listTemplates: async (filters = {}) => {
        let functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates`;
        const queryParams = new URLSearchParams();

        if (filters.entityId) queryParams.append('entityId', filters.entityId);
        if (filters.clientId) queryParams.append('clientId', filters.clientId);
        if (filters.status) queryParams.append('status', filters.status);
        if (filters.templateType) queryParams.append('templateType', filters.templateType);
        if (filters.isGlobal !== undefined) queryParams.append('isGlobal', filters.isGlobal);
        if (filters.categoryId) queryParams.append('categoryId', filters.categoryId);

        const queryString = queryParams.toString();
        if (queryString) {
            functionUrl += `?${queryString}`;
        }

        return fetchWithAuth(functionUrl);
    },

    listTemplateCategories: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates/categories`;
        return fetchWithAuth(functionUrl);
    },

    getEntityBranding: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/branding`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    updateEntityBranding: async (entityId, branding) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/branding/update`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, branding })
        });
    },

    uploadBrandImage: async (formData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/brand/upload`;
        const token = await getAuthToken();

        // For file uploads, don't set Content-Type header - let browser set it with boundary
        const response = await fetch(functionUrl, {
            method: 'POST',
            credentials: 'omit',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            mode: 'cors',
            body: formData // FormData object
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to upload image');
        }

        return await response.json();
    },

    deleteBrandImage: async (entityId, imageType) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/brand/delete`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, imageType })
        });
    },

    generateTemplateWithAI: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates/generate-ai`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    generateReport: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/reports/generate`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    finalizeAIReport: async (sessionId, data) => {
        const functionUrl = `${getApiUrl('documentManagement')}/api/documents/reports/ai-finalize/${sessionId}`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    // Get reports for an entity
    getEntityReports: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/reports?entityId=${entityId}`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    // Delete a document from Firestore
    deleteDocument: async (collection, documentId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/dataManagementApi/api/data/firestore/delete`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({
                collection,
                documentId
            })
        });
    },

    exportToPDF: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/export/pdf`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    getDocumentConfig: async (clientId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/config`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId: clientId })
        });
    },

    updateDocumentConfig: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/config/update`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    // IP Catalog APIs (formerly FIPD Library/Intelligence Hub)
    listFIPDDocuments: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/fipd`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    manageFIPDDocument: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/fipd`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    exportFIPDContent: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/fipd/export`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    deleteFIPDDocument: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/fipd/${data.documentId}`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE'
        });
    },

    initializePlatformFolders: async (forceRefresh = false) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/platform/init${forceRefresh ? '?forceRefresh=true' : ''}`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({})
        });
    },

    initializeDMCollections: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/collections/init`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({})
        });
    },

    // Shared Drive APIs
    listSharedDrives: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/drives/shared`;
        return fetchWithAuth(functionUrl);
    },

    importSharedDriveDocuments: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/drives/import`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    // Template CRUD APIs
    createTemplate: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    getTemplate: async (templateId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates/${templateId}`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    updateTemplate: async (data) => {
        const { templateId, ...updateData } = data;
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates/${templateId}`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify(updateData)
        });
    },

    deleteTemplate: async (templateId, _hardDelete = false) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates/${templateId}`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE'
        });
    },

    // Template locking functions
    acquireTemplateLock: async (templateId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates/${templateId}/lock`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({})
        });
    },

    releaseTemplateLock: async (templateId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates/${templateId}/lock`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE'
        });
    },

    getTemplateLockStatus: async (templateId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates/${templateId}/lock/status`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    // Entity methods
    getEntities: async (params = {}) => {
        // Build query string from params
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.type) queryParams.append('type', params.type);
        if (params.status) queryParams.append('status', params.status);
        if (params.search) queryParams.append('search', params.search);
        if (params.tier) queryParams.append('tier', params.tier);
        if (params.region) queryParams.append('region', params.region);
        if (params.entityId) queryParams.append('entityId', params.entityId);
        if (params.sortBy) queryParams.append('sortBy', params.sortBy);
        if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

        const queryString = queryParams.toString();
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/list${queryString ? '?' + queryString : ''}`;
        const response = await fetchWithAuth(functionUrl);

        // Return the full response including pagination
        return response;
    },

    getEntityById: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/get?entityId=${entityId}`;
        const response = await fetchWithAuth(functionUrl);
        return response.entity || null;
    },

    createEntity: async (entityData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/create`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(entityData)
        });
    },

    updateEntity: async (entityId, updateData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/update-entity`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify({
                entityId,
                ...updateData
            })
        });
    },

    deleteEntity: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/delete-entity`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE',
            body: JSON.stringify({
                entityId
            })
        });
    },

    // Get relationships from Firestore
    getRelationships: async () => {
        // Get relationships from backend endpoint
        try {
            const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/relationships`;
            const response = await fetchWithAuth(functionUrl);
            return response.relationships || [];
        } catch (_error) {
            console.warn('Relationships endpoint not available, using empty array');
            return [];
        }
    },

    // Migration functions
    migrateClientsToEntities: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/migrationApi/api/migration/clients-to-entities`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({})
        });
    },

    checkMigrationStatus: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/migrationApi/api/migration/status`;
        return fetchWithAuth(functionUrl);
    },

    // Chat API methods — batch endpoint removed (F14), all chat uses streaming

    /**
     * Stream chat message via SSE.
     * @param {string} entityId
     * @param {string} chatId
     * @param {string} message
     * @param {object} options - { promptId, noteTakingMode }
     * @param {object} handlers - { onToken, onToolStart, onToolEnd, onError, onDone }
     * @returns {object} - { abort: Function } to cancel the stream
     */
    // eslint-disable-next-line max-params
    streamChatMessage: async (entityId, chatId, message, options = {}, handlers = {}) => {
        const token = await getAuthToken();
        const controller = new AbortController();

        const url = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/stream`;

        const fetchPromise = fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                entityId,
                chatId,
                message,
                promptId: options.promptId,
                noteTakingMode: options.noteTakingMode,
            }),
            signal: controller.signal,
        });

        fetchPromise.then(async (response) => {
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                handlers.onError?.({
                    errorType: errorData.errorType || 'network_error',
                    userMessage: errorData.error || `HTTP ${response.status}`,
                });
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });

                    // Parse SSE events from buffer
                    const events = buffer.split('\n\n');
                    buffer = events.pop() || '';

                    for (const eventStr of events) {
                        // eslint-disable-next-line max-depth
                        if (!eventStr.trim() || eventStr.startsWith(':')) continue;

                        let eventType = 'message';
                        let eventData = '';

                        // eslint-disable-next-line max-depth
                        for (const line of eventStr.split('\n')) {
                            // eslint-disable-next-line max-depth
                            if (line.startsWith('event: ')) {
                                eventType = line.substring(7).trim();
                            } else if (line.startsWith('data: ')) {
                                eventData = eventData ? eventData + '\n' + line.substring(6) : line.substring(6);
                            }
                        }

                        if (!eventData) continue;

                        try {
                            const data = JSON.parse(eventData);

                            // eslint-disable-next-line max-depth
                            switch (eventType) {
                                case 'token':
                                    handlers.onToken?.(data.content);
                                    break;
                                case 'tool_start':
                                    handlers.onToolStart?.(data.toolName);
                                    break;
                                case 'tool_end':
                                    handlers.onToolEnd?.(data);
                                    break;
                                case 'error':
                                    handlers.onError?.(data);
                                    break;
                                case 'done':
                                    handlers.onDone?.(data);
                                    break;
                                default:
                                    break;
                            }
                        } catch (_parseErr) {
                            console.warn('Failed to parse SSE event:', eventStr);
                        }
                    }
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    handlers.onError?.({ errorType: 'network_error', userMessage: err.message });
                }
            }
        }).catch((err) => {
            if (err.name !== 'AbortError') {
                handlers.onError?.({ errorType: 'network_error', userMessage: err.message });
            }
        });

        return { abort: () => controller.abort() };
    },

    saveReportMessage: async (entityId, chatId, messageData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/saveReportMessage`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, chatId, messageData })
        });
    },

    listChats: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/list`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    getChatHistory: async (entityId, chatId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/history`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, chatId })
        });
    },

    createChat: async (entityId, title) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, title })
        });
    },

    deleteChat: async (entityId, chatId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/delete`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE',
            body: JSON.stringify({ entityId, chatId })
        });
    },

    updateChatTitle: async (entityId, chatId, title) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/${chatId}/title`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify({ entityId, title })
        });
    },

    getChatMetadata: async (entityId, chatId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/metadata`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, chatId })
        });
    },

    listMCPResources: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/mcp/resources?entityId=${entityId}`;
        return fetchWithAuth(functionUrl);
    },

    readMCPResource: async (entityId, resourceId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/mcp/resources/${encodeURIComponent(resourceId)}?entityId=${entityId}`;
        return fetchWithAuth(functionUrl);
    },

    callMCPTemplateTool: async (toolName, toolArgs) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/mcp/templates/call`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ toolName, arguments: toolArgs })
        });
    },

    listMCPTemplateTools: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/chatMcpApi/api/chat/mcp/templates/tools`;
        return fetchWithAuth(functionUrl);
    },

    // Notes API methods
    createManualNote: async (noteData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(noteData)
        });
    },

    updateNoteContent: async (noteData) => {
        const { noteId, ...bodyData } = noteData;
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes/${noteId}/content`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify(bodyData)
        });
    },

    listEntityNotes: async (entityId, filters = {}) => {
        const params = new URLSearchParams(filters);
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes/entities/${entityId}${params.toString() ? `?${params}` : ''}`;
        return fetchWithAuth(functionUrl);
    },

    getNote: async (noteId, entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes/${noteId}?entityId=${entityId}`;
        return fetchWithAuth(functionUrl);
    },

    updateNotePermissions: async (permissionData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes/${permissionData.noteId}/permissions`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify(permissionData)
        });
    },

    deleteNote: async (noteId, entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes/${noteId}`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE',
            body: JSON.stringify({ entityId })
        });
    },

    // Get signed URL for audio playback from a note
    // gcsPath is optional - used for legacy notes without audioMetadata field
    getNoteAudioUrl: async (noteId, entityId, gcsPath = null) => {
        let functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes/${noteId}/audio-url?entityId=${entityId}`;
        if (gcsPath) {
            functionUrl += `&gcsPath=${encodeURIComponent(gcsPath)}`;
        }
        return fetchWithAuth(functionUrl);
    },

    // File Upload API
    uploadFile: async (entityId, file, metadata = {}) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityId', entityId);
        if (Object.keys(metadata).length > 0) {
            formData.append('metadata', JSON.stringify(metadata));
        }

        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ingestionApi/api/ingestion/files`;

        // Get auth token
        const token = await getAuthToken();

        // Make request with FormData (don't set Content-Type - browser sets it with boundary)
        const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData,
            credentials: 'omit',
            mode: 'cors'
        });

        if (!response.ok) {
            let errorMessage;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorData.error || `Error: ${response.status} ${response.statusText}`;
            } catch (_e) {
                errorMessage = `Error: ${response.status} ${response.statusText}`;
            }
            throw new Error(errorMessage);
        }

        return await response.json();
    },

    // Note Attachments API
    createNoteAttachment: async (noteId, entityId, fileData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes/${noteId}/attachments`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, ...fileData })
        });
    },

    getNoteAttachments: async (noteId, entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes/${noteId}/attachments?entityId=${entityId}`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    deleteNoteAttachment: async (noteId, attachmentId, entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/notesApi/api/notes/${noteId}/attachments/${attachmentId}?entityId=${entityId}`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE'
        });
    },

    // Prompt Management API methods
    createPrompt: async (promptData) => {
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/create`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(promptData)
        });
    },

    listPrompts: async (filters = {}) => {
        let functionUrl = `${getApiUrl('promptManagement')}/api/prompts/list`;
        const queryParams = new URLSearchParams();

        if (filters.category) queryParams.append('category', filters.category);
        if (filters.isActive !== undefined) queryParams.append('isActive', filters.isActive);
        if (filters.includeVersions) queryParams.append('includeVersions', filters.includeVersions);

        const queryString = queryParams.toString();
        if (queryString) {
            functionUrl += `?${queryString}`;
        }

        return fetchWithAuth(functionUrl);
    },

    getPromptCategories: async () => {
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/categories`;
        return fetchWithAuth(functionUrl);
    },

    getPrompt: async (promptId) => {
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/${promptId}`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    updatePrompt: async (promptData) => {
        const { promptId, ...updateData } = promptData;
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/${promptId}/update`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(updateData)
        });
    },

    deletePrompt: async (promptId) => {
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/${promptId}/delete`;
        return fetchWithAuth(functionUrl, {
            method: 'POST'
        });
    },

    getDefaultPrompt: async (category) => {
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/default?category=${encodeURIComponent(category)}`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    initializeDefaultPrompts: async () => {
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/initialize`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({})
        });
    },

    getPromptVersions: async (promptName) => {
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/versions?promptName=${encodeURIComponent(promptName)}`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    restorePromptVersion: async (promptId) => {
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/${promptId}/restore`;
        return fetchWithAuth(functionUrl, {
            method: 'POST'
        });
    },

    deletePromptVersion: async (promptId) => {
        const functionUrl = `${getApiUrl('promptManagement')}/api/prompts/${promptId}/delete-version`;
        return fetchWithAuth(functionUrl, {
            method: 'POST'
        });
    },

    // Detokenization API
    detokenizeContent: async (entityId, tokenizedContent) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ptlApi/api/ptl/detokenize`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, tokenizedContent })
        });
    },

    // Privacy Tokenization Layer (PTL) functions
    processEntityDocuments: async (entityId, forceReprocess = false) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ptlApi/api/ptl/process`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, forceReprocess })
        });
    },

    getProcessingStatus: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/ptlApi/api/ptl/status`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    // Entity Discovery Management APIs
    getPendingEntityDiscoveries: async (params = {}) => {
        const queryString = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
        ).toString();
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/discoveries/pending${queryString ? `?${queryString}` : ''}`;
        return fetchWithAuth(functionUrl);
    },

    getEntityMatchSuggestions: async (discoveredEntityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/matches/suggestions`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ discoveredEntityId })
        });
    },

    confirmEntityTokenAssociation: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/matches/confirm`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    createEntityFromDiscovery: async (discoveryId, data = {}) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/discoveries/${discoveryId}/create-entity`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    rejectDiscoveredEntity: async (discoveryId, data = {}) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/discoveries/${discoveryId}/reject`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    // Brand Asset Management APIs
    listBrandAssets: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/assets/list`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    uploadBrandAsset: async (formData) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/assets/upload`;
        const token = await getAuthToken();

        // For file uploads, don't set Content-Type header - let browser set it with boundary
        const response = await fetch(functionUrl, {
            method: 'POST',
            credentials: 'omit',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            mode: 'cors',
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to upload asset');
        }

        return await response.json();
    },

    deleteBrandAsset: async (entityId, assetId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/assets/delete`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE',
            body: JSON.stringify({ entityId, assetId })
        });
    },

    getColorScheme: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/colors`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    updateColorScheme: async (entityId, colorScheme) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/colors/update`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, colorScheme })
        });
    },

    // Admin API methods
    triggerBackup: async (includeGCS = true, includeBigQuery = true) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/adminApi/api/admin/backup/trigger`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ includeGCS, includeBigQuery })
        });
    },

    getBackupStatus: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/adminApi/api/admin/backup/status`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({})
        });
    },

    listBackups: async (limit = 10) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/adminApi/api/admin/backup/list`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ limit })
        });
    },

    // Token Examiner API methods
    getTokenExaminationStatus: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/status`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    examineTokensManual: async (entityId, tokenIds = []) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/examine`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, tokenIds })
        });
    },

    analyzeEntityRelationships: async (entityId, options = {}) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/relationships/analyze`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({
                entityId,
                analysisDepth: options.analysisDepth || 'COMPREHENSIVE',
                focusRelationships: options.focusRelationships,
                includeHealth: options.includeHealth !== false,
                includeNetworkAnalysis: options.includeNetworkAnalysis || false
            })
        });
    },

    getEntityRelationships: async (entityId, params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.type) queryParams.append('type', params.type);
        if (params.limit) queryParams.append('limit', params.limit);
        if (params.includeAssessment) queryParams.append('includeAssessment', params.includeAssessment);
        if (params.minHealth !== undefined) queryParams.append('minHealth', params.minHealth);
        if (params.maxHealth !== undefined) queryParams.append('maxHealth', params.maxHealth);
        if (params.sortBy) queryParams.append('sortBy', params.sortBy);

        const queryString = queryParams.toString();
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/entities/${entityId}/relationships${queryString ? '?' + queryString : ''}`;
        return fetchWithAuth(functionUrl);
    },

    assessRelationshipHealth: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/relationships/health`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    confirmRelationship: async (relationshipId, entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/matches/confirm`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ matchId: relationshipId, entityId })
        });
    },

    rejectRelationship: async (relationshipId, entityId, reason) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/matches/reject`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ matchId: relationshipId, entityId, reason })
        });
    },

    batchConfirmRelationships: async (relationshipIds, entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/matches/bulk-confirm`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ matchIds: relationshipIds, entityId })
        });
    },

    getTemporalAnalysis: async (entityId, startDate, endDate) => {
        const params = new URLSearchParams({ entityId });
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/temporal/analysis?${params}`;
        return fetchWithAuth(functionUrl);
    },

    createTemporalSnapshot: async (entityId, notes) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/temporal/snapshot`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, notes })
        });
    },

    syncEntityEmbeddings: async (entityId, forceRegenerate = false) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/embeddings/sync`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, forceRegenerate })
        });
    },

    /**
     * Get network graph data from relationship rollups for SNA visualization
     * @param {string} entityId - The entity ID
     * @returns {Promise<{nodes: Array, edges: Array, metadata: Object}>}
     */
    getNetworkGraphData: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/entities/${entityId}/network-graph`;
        return fetchWithAuth(functionUrl);
    },

    getExaminationHistory: async (tokenId, limit = 10) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/examination-history?tokenId=${tokenId}&limit=${limit}`;
        return fetchWithAuth(functionUrl);
    },

    forceReexamination: async (tokenId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/force-reexamination`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ tokenId })
        });
    },

    getExaminationStats: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/examination-stats?entityId=${entityId}`;
        return fetchWithAuth(functionUrl);
    },

    createCrossEntityLinkage: async (data) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/linkages`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    getEntityLinkages: async (entityId, limit = 50) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/entities/${entityId}/linkages?limit=${limit}`;
        return fetchWithAuth(functionUrl);
    },

    getPendingDiscoveries: async () => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/discoveries/pending`;
        return fetchWithAuth(functionUrl);
    },

    reviewDiscovery: async (discoveryId, action, data = {}) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/tokenExaminerApi/api/token-examiner/discoveries/${discoveryId}/review`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ action, ...data })
        });
    },

    // Shared Drives API methods
    getSharedDrives: async (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives${queryString ? `?${queryString}` : ''}`;
        return fetchWithAuth(functionUrl);
    },

    getSharedDriveDetails: async (driveId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/${driveId}`;
        return fetchWithAuth(functionUrl);
    },

    searchSharedDrive: async (driveId, searchQuery, params = {}) => {
        const queryString = new URLSearchParams({ q: searchQuery, ...params }).toString();
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/${driveId}/search?${queryString}`;
        return fetchWithAuth(functionUrl);
    },

    getFolderContents: async (driveId, folderId, params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/${driveId}/folders/${folderId}/contents${queryString ? `?${queryString}` : ''}`;
        console.log(`[API.getFolderContents] Requesting folder contents:`, {
            driveId,
            folderId,
            params,
            url: functionUrl
        });
        const result = await fetchWithAuth(functionUrl);
        console.log(`[API.getFolderContents] Response received:`, {
            folders: result?.folders?.length || 0,
            files: result?.files?.length || 0,
            hasData: !!(result?.folders || result?.files),
            result
        });
        return result;
    },

    getFolderPath: async (driveId, folderId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/${driveId}/path/${folderId}`;
        return fetchWithAuth(functionUrl);
    },

    // Shared Drive Backup API methods
    setupSharedDriveBackup: async (driveId, driveName, enableScheduledBackup = false) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/${driveId}/backup/setup`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ driveName, enableScheduledBackup })
        });
    },

    getSharedDriveBackupStatus: async (driveId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/${driveId}/backup/status`;
        return fetchWithAuth(functionUrl);
    },

    triggerSharedDriveBackup: async (driveId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/${driveId}/backup/trigger`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({})
        });
    },

    toggleSharedDriveBackup: async (driveId, enabled) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/${driveId}/backup/toggle`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify({ enabled })
        });
    },

    getSharedDriveBackupStatusBatch: async (driveIds) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/backup/status-batch`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ driveIds })
        });
    },
    searchAllSharedDrives: async (searchQuery, options = {}) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/auth/shared-drives/search-all`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({
                searchQuery,
                driveIds: options.driveIds,
                fileTypes: options.fileTypes,
                maxResultsPerDrive: options.maxResultsPerDrive || 10
            })
        });
    },

    // Alias for listPrompts to match UI usage
    getPrompts: async (filters = {}) => {
        return api.listPrompts(filters);
    },

    // Document Templates API methods
    getDocumentTemplates: async (entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/documentManagementApi/api/documents/templates/list`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    // AI Report Generation API methods
    generateAIReport: async (params) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/aiReportingApi/api/aiReporting/initialize`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(params)
        });
    },

    getReportStatus: async (reportId, entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/aiReportingApi/api/aiReporting/status/${reportId}?entityId=${encodeURIComponent(entityId)}`;
        return fetchWithAuth(functionUrl);
    },

    getReportQuickStatus: async (reportId, entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/aiReportingApi/api/aiReporting/quickStatus/${reportId}?entityId=${encodeURIComponent(entityId)}`;
        return fetchWithAuth(functionUrl);
    },

    // Alias for compatibility with existing ChatBot code
    getReportGenerationStatus: async (reportId) => {
        // This will be updated to poll the new PV report_building data
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/aiReportingApi/api/aiReporting/status/${reportId}`;
        return fetchWithAuth(functionUrl);
    },

    continueReportGeneration: async (reportId, params) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/aiReportingApi/api/aiReporting/continue/${reportId}`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(params)
        });
    },

    compileReport: async (reportId, entityId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/aiReportingApi/api/aiReporting/compile/${reportId}`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    // User Settings API
    getUserSettings: async () => {
        const url = `${getApiUrl('userSettings')}/api/user/settings`;
        return fetchWithAuth(url);
    },

    updateUserSettings: async (settings) => {
        const url = `${getApiUrl('userSettings')}/api/user/settings`;
        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify(settings)
        });
    },

    updateUserSettingsCategory: async (category, settings) => {
        const url = `${getApiUrl('userSettings')}/api/user/settings/${category}`;
        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify(settings)
        });
    },

    resetUserSettings: async () => {
        const url = `${getApiUrl('userSettings')}/api/user/settings/reset`;
        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify({})
        });
    },

    // Generic HTTP methods for admin API and other dynamic endpoints
    get: async (path) => {
        // If path starts with /, it includes the API name
        let url;
        if (path.startsWith('/')) {
            // Extract API name from path like /adminApi/api/admin/verify
            const apiNameWithSuffix = path.split('/')[1]; // e.g., 'adminApi'
            const apiName = apiNameWithSuffix.replace(/Api$/, ''); // Remove 'Api' suffix to get 'admin'
            const remainingPath = path.substring(path.indexOf('/', 1)); // e.g., '/api/admin/verify'
            url = `${getApiUrl(apiName)}${remainingPath}`;
        } else {
            url = path;
        }
        console.log(`GET request to: ${url}`);
        return fetchWithAuth(url, {
            method: 'GET'
        });
    },

    post: async (path, data) => {
        // If path starts with /, it includes the API name
        let url;
        if (path.startsWith('/')) {
            // Extract API name from path like /adminApi/api/admin/es-browser/search
            const apiNameWithSuffix = path.split('/')[1]; // e.g., 'adminApi'
            const apiName = apiNameWithSuffix.replace(/Api$/, ''); // Remove 'Api' suffix to get 'admin'
            const remainingPath = path.substring(path.indexOf('/', 1)); // e.g., '/api/admin/es-browser/search'
            url = `${getApiUrl(apiName)}${remainingPath}`;
        } else {
            url = path;
        }
        console.log(`POST request to: ${url}`);
        return fetchWithAuth(url, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    put: async (path, data) => {
        // If path starts with /, it includes the API name
        const url = path.startsWith('/')
            ? `${getApiUrl(path.split('/')[1])}${path.substring(path.indexOf('/', 1))}`
            : path;
        return fetchWithAuth(url, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    },

    delete: async (path, data) => {
        // If path starts with /, it includes the API name
        const url = path.startsWith('/')
            ? `${getApiUrl(path.split('/')[1])}${path.substring(path.indexOf('/', 1))}`
            : path;
        return fetchWithAuth(url, {
            method: 'DELETE',
            body: data ? JSON.stringify(data) : undefined
        });
    },

    // CSV Import API methods
    checkEntityDuplicates: async (entities) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/check-duplicates`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entities }) // Backend expects { entities: PersonEntity[] }
        });
    },

    bulkImportEntities: async (importRequest) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/bulk-import`;
        // Ensure we send exactly what backend expects: { data, mappings, importType, userId, userEmail }
        const { data, mappings, importType, userId, userEmail } = importRequest;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ data, mappings, importType, userId, userEmail })
        });
    },

    getImportProgress: async (importId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/import-progress/${importId}`;
        return fetchWithAuth(functionUrl);
    },

    cancelImport: async (importId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/import/${importId}/cancel`;
        return fetchWithAuth(functionUrl, {
            method: 'POST'
        });
    },

    // Get user roles for authorization checking
    getUserRoles: async (userId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/authWorkspaceApi/api/user/roles/${userId}`;
        return fetchWithAuth(functionUrl);
    },

    // Rollback import - PERMANENTLY DELETES all entities created during import
    rollbackImport: async (importId) => {
        const functionUrl = `${import.meta.env.VITE_FUNCTIONS_BASE_URL}/entityManagementApi/api/entities/import/${importId}/rollback`;
        return fetchWithAuth(functionUrl, {
            method: 'POST'
        });
    },

    // Admin Rollback Management APIs

    // Get rollback statistics and audit history
    getRollbackHistory: async (params = {}) => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/audit/rollbacks`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(params)
        });
    },

    // Get imports available for rollback
    getRollbackableImports: async (params = {}) => {
        const functionUrl = `${getApiUrl('entityManagement')}/api/entities/imports/rollbackable`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(params)
        });
    },

    // Execute rollback with comprehensive audit logging
    executeRollbackWithAudit: async (rollbackData) => {
        const functionUrl = `${getApiUrl('entityManagement')}/api/entities/rollback-with-audit`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(rollbackData)
        });
    },

    // Get rollback impact assessment
    getRollbackImpactAssessment: async (importId) => {
        const functionUrl = `${getApiUrl('entityManagement')}/api/entities/import/${importId}/impact-assessment`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    // Admin approval for large rollbacks
    approveRollback: async (importId, approvalData) => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/approve`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ importId, ...approvalData })
        });
    },

    // Reject rollback request
    rejectRollback: async (importId, rejectionData) => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/reject`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ importId, ...rejectionData })
        });
    },

    // Get rollback compliance report
    generateRollbackReport: async (reportParams) => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/report`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(reportParams)
        });
    },

    // Slack Ingestion API methods
    listSlackWorkspaces: async (entityId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/workspaces/list`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    initializeSlackAuth: async (entityId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/auth/initialize`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    refreshSlackWorkspace: async (entityId, workspaceId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/workspaces/refresh`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, workspaceId })
        });
    },

    disconnectSlackWorkspace: async (entityId, workspaceId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/workspaces/disconnect`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, workspaceId })
        });
    },

    listSlackChannels: async (entityId, workspaceId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/channels/list`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, workspaceId })
        });
    },

    refreshSlackChannels: async (entityId, workspaceId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/channels/refresh`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, workspaceId })
        });
    },

    getSlackBotStatus: async (entityId, ingestionPointId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/bot/status`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, ingestionPointId })
        });
    },

    updateSlackBotConfig: async (entityId, ingestionPointId, config) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/bot/config`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify({ entityId, ingestionPointId, config })
        });
    },

    pauseSlackBot: async (entityId, ingestionPointId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/bot/pause`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, ingestionPointId })
        });
    },

    resumeSlackBot: async (entityId, ingestionPointId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/bot/resume`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, ingestionPointId })
        });
    },

    deleteSlackBot: async (entityId, ingestionPointId) => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/bot/delete`;
        return fetchWithAuth(functionUrl, {
            method: 'DELETE',
            body: JSON.stringify({ entityId, ingestionPointId })
        });
    },

    getSlackStats: async (entityId, ingestionPointId, timeRange = '7d') => {
        const functionUrl = `${getApiUrl('slackIngestion')}/api/slack/stats`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, ingestionPointId, timeRange })
        });
    },

    // Slack Entity Creation API methods
    createSlackEntity: async (entityData) => {
        const functionUrl = `${getApiUrl('slackEvent')}/api/slack/entities/create`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(entityData)
        });
    },

    approveSlackEntity: async (entityId, discoveredEntityId) => {
        const functionUrl = `${getApiUrl('slackEvent')}/api/slack/entities/approve`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, discoveredEntityId })
        });
    },

    rejectSlackEntity: async (entityId, discoveredEntityId, reason) => {
        const functionUrl = `${getApiUrl('slackEvent')}/api/slack/entities/reject`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, discoveredEntityId, reason })
        });
    },

    listPendingSlackEntities: async (entityId) => {
        const functionUrl = `${getApiUrl('slackEvent')}/api/slack/entities/pending`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    // Bulk rollback management
    bulkRollbackOperations: async (operations) => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/bulk`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ operations })
        });
    },

    // Get rollback governance settings
    getRollbackGovernanceSettings: async () => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/governance`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    // Update rollback governance settings
    updateRollbackGovernanceSettings: async (settings) => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/governance`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify(settings)
        });
    },

    // Get rollback notifications
    getRollbackNotifications: async (params = {}) => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/notifications`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(params)
        });
    },

    // Mark rollback notifications as read
    markRollbackNotificationsRead: async (notificationIds) => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/notifications/read`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ notificationIds })
        });
    },

    // Get rollback time limits and safety settings
    getRollbackSafetySettings: async () => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/safety`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    // Update rollback time limits and safety settings
    updateRollbackSafetySettings: async (settings) => {
        const functionUrl = `${getApiUrl('admin')}/api/admin/rollback/safety`;
        return fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify(settings)
        });
    },

    // Dashboard Report Generation APIs

    // Generate dashboard report using AI
    generateDashboardReport: async (params) => {
        const functionUrl = `${getApiUrl('documentManagement')}/api/documents/dashboard-generate`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(params)
        });
    },

    // Get dashboard generation progress
    getDashboardGenerationProgress: async (sessionId) => {
        const functionUrl = `${getApiUrl('documentManagement')}/api/documents/dashboard-progress/${sessionId}`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    // Get cached dashboard data
    getDashboardData: async (entityId) => {
        const functionUrl = `${getApiUrl('documentManagement')}/api/documents/dashboard-data/${entityId}`;
        return fetchWithAuth(functionUrl, {
            method: 'GET'
        });
    },

    // Token Examiner Dialogue API methods
    getTeExecutions: async (entityId, filters) => {
        const functionUrl = `${getApiUrl('tokenExaminer')}/api/token-examiner/dialogue/executions`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId, ...filters })
        });
    },

    getTeExecutionDialogue: async (entityId, executionId) => {
        const functionUrl = `${getApiUrl('tokenExaminer')}/api/token-examiner/dialogue/executions/${executionId}`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({ entityId })
        });
    },

    // SNA (Social Network Analysis) API methods

    /**
     * Get SNA impact summary for an entity, formatted for prompt injection preview
     * @param {string} entityId - The entity ID to get impact data for
     * @returns {Promise<{success: boolean, entityId: string, summary: string, stats: Object, recentDocuments: Array}>}
     */
    getSNAImpactPreview: async (entityId) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/entity/${entityId}/impact`;
        return fetchWithAuth(functionUrl);
    },

    /**
     * Query the SNA vector store with semantic similarity search
     * @param {Object} queryRequest - Query parameters
     * @param {string} queryRequest.query - The search query
     * @param {string} queryRequest.entityId - The entity ID to search within
     * @param {number} [queryRequest.limit] - Maximum number of results
     * @param {boolean} [queryRequest.includeFIPD] - Include FIPD library results
     * @param {boolean} [queryRequest.includeContent] - Include document content in results
     * @returns {Promise<Object>} - Query results with vector similarity matches
     */
    querySNAVectorStore: async (queryRequest) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/query`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(queryRequest)
        });
    },

    /**
     * Get SNA vector store statistics
     * @param {string} [entityId] - Optional entity ID for entity-specific stats (admin only for global stats)
     * @returns {Promise<Object>} - Collection statistics
     */
    getSNAStats: async (entityId = null) => {
        let functionUrl = `${getApiUrl('sna')}/api/sna/stats`;
        if (entityId) {
            functionUrl += `?entityId=${encodeURIComponent(entityId)}`;
        }
        return fetchWithAuth(functionUrl);
    },

    /**
     * Check SNA service health status
     * @returns {Promise<Object>} - Health status for vector indexes and embedding service
     */
    getSNAHealth: async () => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/health`;
        return fetchWithAuth(functionUrl);
    },

    // ============================================================================
    // SNA Behavioral Code Operations
    // ============================================================================

    /**
     * List all behavioral codes
     * @param {Object} options - Query options
     * @param {string} [options.category] - Filter by category
     * @param {string} [options.dimension] - Filter by primary dimension
     * @param {boolean} [options.includeInactive] - Include inactive codes
     * @returns {Promise<Array>} - List of behavioral codes
     */
    listBehavioralCodes: async (options = {}) => {
        const params = new URLSearchParams();
        if (options.category) params.append('category', options.category);
        if (options.dimension) params.append('dimension', options.dimension);
        if (options.includeInactive) params.append('includeInactive', 'true');

        const queryString = params.toString();
        const functionUrl = `${getApiUrl('sna')}/api/sna/codes${queryString ? `?${queryString}` : ''}`;
        const result = await fetchWithAuth(functionUrl);
        return result.codes || [];
    },

    /**
     * Get a single behavioral code by ID
     * @param {string} codeId - The code ID
     * @returns {Promise<Object>} - The behavioral code
     */
    getBehavioralCodeById: async (codeId) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/codes/${encodeURIComponent(codeId)}`;
        const result = await fetchWithAuth(functionUrl);
        return result.code;
    },

    /**
     * Get behavioral code categories with counts
     * @returns {Promise<Array>} - List of categories
     */
    getBehavioralCategories: async () => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/codes/categories`;
        const result = await fetchWithAuth(functionUrl);
        return result.categories || [];
    },

    /**
     * Get behavioral dimensions with mapped codes
     * @returns {Promise<Array>} - List of dimensions
     */
    getBehavioralDimensions: async () => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/codes/dimensions`;
        const result = await fetchWithAuth(functionUrl);
        return result.dimensions || [];
    },

    /**
     * Create a new behavioral code (admin only)
     * @param {Object} codeData - The code data
     * @returns {Promise<Object>} - The created code
     */
    createBehavioralCode: async (codeData) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/codes`;
        const result = await fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(codeData),
        });
        return result.code;
    },

    /**
     * Update a behavioral code (admin only)
     * @param {string} codeId - The code ID
     * @param {Object} updates - The updates to apply
     * @returns {Promise<Object>} - The updated code
     */
    updateBehavioralCode: async (codeId, updates) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/codes/${encodeURIComponent(codeId)}`;
        const result = await fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
        return result.code;
    },

    /**
     * Delete (soft) a behavioral code (admin only)
     * @param {string} codeId - The code ID
     * @returns {Promise<void>}
     */
    deleteBehavioralCode: async (codeId) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/codes/${encodeURIComponent(codeId)}`;
        await fetchWithAuth(functionUrl, { method: 'DELETE' });
    },

    // ============================================================================
    // SNA Chunk Code Operations
    // ============================================================================

    /**
     * Get behavioral codes applied to a chunk
     * @param {string} chunkId - The chunk ID
     * @returns {Promise<Array>} - List of applied codes
     */
    getChunkBehavioralCodes: async (chunkId) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/chunks/${encodeURIComponent(chunkId)}/codes`;
        const result = await fetchWithAuth(functionUrl);
        return result.codes || [];
    },

    /**
     * Apply a behavioral code to a chunk
     * @param {string} chunkId - The chunk ID
     * @param {Object} codeData - Code application data
     * @param {string} codeData.code - The code name
     * @param {number} [codeData.confidence] - Confidence score (0-1)
     * @param {string} [codeData.notes] - Optional notes
     * @param {string} [codeData.taxonomy] - Optional taxonomy (default: faana_core)
     * @returns {Promise<Array>} - Updated list of applied codes
     */
    applyBehavioralCodeToChunk: async (chunkId, codeData) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/chunks/${encodeURIComponent(chunkId)}/codes`;
        const result = await fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify(codeData),
        });
        return result.codes || [];
    },

    /**
     * Update a code on a chunk (notes, confidence)
     * @param {string} chunkId - The chunk ID
     * @param {string} codeName - The code name
     * @param {Object} updates - The updates to apply
     * @returns {Promise<Array>} - Updated list of applied codes
     */
    updateChunkBehavioralCode: async (chunkId, codeName, updates) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/chunks/${encodeURIComponent(chunkId)}/codes/${encodeURIComponent(codeName)}`;
        const result = await fetchWithAuth(functionUrl, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
        return result.codes || [];
    },

    /**
     * Remove a code from a chunk
     * @param {string} chunkId - The chunk ID
     * @param {string} codeName - The code name
     * @returns {Promise<Array>} - Updated list of applied codes
     */
    removeChunkBehavioralCode: async (chunkId, codeName) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/chunks/${encodeURIComponent(chunkId)}/codes/${encodeURIComponent(codeName)}`;
        const result = await fetchWithAuth(functionUrl, { method: 'DELETE' });
        return result.codes || [];
    },

    // ============================================================================
    // SNA Document/Chunk Search Operations
    // ============================================================================

    /**
     * Search documents with behavioral code filters
     * @param {string} entityId - Entity ID
     * @param {Object} options - Search options
     * @returns {Promise<Object>} - Search results
     */
    searchBehavioralDocuments: async (entityId, options = {}) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/search`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({
                entityId,
                ...options,
            }),
        });
    },

    /**
     * Get all chunks for a document
     * @param {string} entityId - Entity ID
     * @param {string} sourceId - The document source ID
     * @returns {Promise<Array>} - List of chunks
     */
    getDocumentBehavioralChunks: async (entityId, sourceId) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/search`;
        const result = await fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({
                entityId,
                sourceId,
                size: 1000,
            }),
        });
        return result.chunks || [];
    },

    /**
     * Get documents for an entity (unique source IDs)
     * @param {string} entityId - Entity ID
     * @returns {Promise<Array>} - List of documents/sources
     */
    getEntityBehavioralDocuments: async (entityId) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/search`;
        const result = await fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({
                entityId,
                aggregateBySource: true,
                size: 100,
            }),
        });
        return result.sources || result.chunks || [];
    },

    // ============================================================================
    // SNA AI Suggestion Operations
    // ============================================================================

    /**
     * Get AI suggestions for behavioral codes on a chunk
     * @param {string} chunkId - The chunk ID
     * @param {string} entityId - Entity ID for access validation
     * @param {Object} options - Optional parameters
     * @param {boolean} [options.includeExplanation] - Include reasoning (default: true)
     * @param {number} [options.minConfidence] - Minimum confidence (default: 0.5)
     * @param {string} [options.entityContext] - Speaker/subject context
     * @returns {Promise<Object>} - AI suggestions
     */
    getBehavioralAISuggestions: async (chunkId, entityId, options = {}) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/chunks/${encodeURIComponent(chunkId)}/ai-suggest`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({
                entityId,
                includeExplanation: options.includeExplanation ?? true,
                minConfidence: options.minConfidence ?? 0.5,
                entityContext: options.entityContext,
            }),
        });
    },

    /**
     * Get AI suggestions for all chunks in a document
     * @param {string} sourceId - The document source ID
     * @param {string} entityId - Entity ID for access validation
     * @param {Object} options - Optional parameters
     * @returns {Promise<Object>} - Bulk AI suggestions
     */
    getBulkBehavioralAISuggestions: async (sourceId, entityId, options = {}) => {
        const functionUrl = `${getApiUrl('sna')}/api/sna/documents/${encodeURIComponent(sourceId)}/ai-suggest-bulk`;
        return fetchWithAuth(functionUrl, {
            method: 'POST',
            body: JSON.stringify({
                entityId,
                includeExplanation: options.includeExplanation ?? false,
                minConfidence: options.minConfidence ?? 0.6,
            }),
        });
    },

    // ============================================================================
    // SNA Behavioral Scoring Operations
    // ============================================================================

    /**
     * Get behavioral scores for an entity
     * @param {string} entityId - Entity ID
     * @param {Object} options - Optional parameters
     * @param {number} [options.timeWindow] - 30, 60, 90, or 120 days (default: 90)
     * @param {boolean} [options.includeDimensions] - Include per-dimension scores (default: true)
     * @param {boolean} [options.includeHistory] - Include historical scores (default: false)
     * @param {boolean} [options.includeCodeDistribution] - Include top codes (default: true)
     * @returns {Promise<Object>} - Entity behavioral scores
     */
    getEntityBehavioralScores: async (entityId, options = {}) => {
        const params = new URLSearchParams();
        if (options.timeWindow) params.append('timeWindow', options.timeWindow.toString());
        if (options.includeDimensions !== undefined) {
            params.append('includeDimensions', options.includeDimensions.toString());
        }
        if (options.includeHistory !== undefined) {
            params.append('includeHistory', options.includeHistory.toString());
        }
        if (options.includeCodeDistribution !== undefined) {
            params.append('includeCodeDistribution', options.includeCodeDistribution.toString());
        }

        const queryString = params.toString();
        const functionUrl = `${getApiUrl('sna')}/api/sna/entity/${encodeURIComponent(entityId)}/scores${queryString ? `?${queryString}` : ''}`;
        return fetchWithAuth(functionUrl);
    }
};

export { fetchWithAuth };
export default api;